import type { GameMode } from '@shared/chart';

/**
 * Per-mode tutorial completion. Each mode (Pulse, Circles, Piano,
 * Guitar, Waves, Jump) plays differently enough that "I've seen Pulse's
 * tutorial" doesn't cover the others, so we key the flag by mode. The
 * legacy `gd-tutorial-completed` key (pre-Circles) and the earlier
 * `gd-tutorial-completed:pulse:v1` slot (pre-rename) are honoured as
 * one-time backfills into the Pulse slot so existing players don't
 * get re-onboarded after either migration.
 *
 * Jump uses a `:v2` slot deliberately: `gd-tutorial-completed:jump:v1`
 * predates this mode and means "saw the Pulse tutorial back when Pulse
 * was called Jump" (it backfills Pulse below), so reusing it would make
 * the cube-runner tutorial silently count as already-seen.
 */
const TUTORIAL_KEYS: Record<GameMode, string> = {
  pulse: 'gd-tutorial-completed:pulse:v1',
  circles: 'gd-tutorial-completed:circles:v1',
  piano: 'gd-tutorial-completed:piano:v1',
  guitar: 'gd-tutorial-completed:guitar:v1',
  waves: 'gd-tutorial-completed:waves:v1',
  jump: 'gd-tutorial-completed:jump:v2',
};

const LEGACY_TUTORIAL_KEY = 'gd-tutorial-completed';
const LEGACY_JUMP_TUTORIAL_KEY = 'gd-tutorial-completed:jump:v1';

/**
 * Circles has a second, opt-in sub-tutorial that teaches sliders. Unlike
 * the per-mode "how to play" flow it is gated on *content*, not on first
 * arrival: a player who stays on the slider-free `normal` tier never sees
 * it, and it fires the first time they're about to play any circles chart
 * that actually contains a slider (i.e. hard/extreme/insane). Keyed
 * separately from the mode tutorial so seeing one doesn't suppress the
 * other.
 */
const CIRCLES_SLIDER_TUTORIAL_KEY = 'gd-tutorial-completed:circles-slider:v1';

function backfillLegacyPulseFlag(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  if (localStorage.getItem(TUTORIAL_KEYS.pulse) === '1') {
    return;
  }
  // Honour both the very-original key (pre per-mode split) and the
  // post-split-but-pre-rename key (when this mode was still called Jump).
  if (
    localStorage.getItem(LEGACY_TUTORIAL_KEY) === '1' ||
    localStorage.getItem(LEGACY_JUMP_TUTORIAL_KEY) === '1'
  ) {
    localStorage.setItem(TUTORIAL_KEYS.pulse, '1');
  }
}

export function isTutorialUnseen(mode: GameMode): boolean {
  if (typeof localStorage === 'undefined') {
    return false;
  }
  if (mode === 'pulse') {
    backfillLegacyPulseFlag();
  }
  return localStorage.getItem(TUTORIAL_KEYS[mode]) !== '1';
}

export function markTutorialSeen(mode: GameMode): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.setItem(TUTORIAL_KEYS[mode], '1');
}

/**
 * In-memory backstop for the slider tutorial flag. The play-page gate
 * re-reads the flag on every chart load, so if the persisted write below
 * silently fails (Safari private mode, quota exceeded, storage disabled
 * by policy) the player would otherwise bounce tutorial → song → tutorial
 * forever — locked out of every slider-bearing map. Remembering "seen"
 * for the session guarantees the gate fires at most once even when the
 * write never lands.
 */
let sliderTutorialSeenThisSession = false;

/**
 * Whether the player still needs the Circles slider sub-tutorial. See
 * {@link CIRCLES_SLIDER_TUTORIAL_KEY}.
 */
export function isCirclesSliderTutorialUnseen(): boolean {
  if (sliderTutorialSeenThisSession) {
    return false;
  }
  if (typeof localStorage === 'undefined') {
    return false;
  }
  return localStorage.getItem(CIRCLES_SLIDER_TUTORIAL_KEY) !== '1';
}

export function markCirclesSliderTutorialSeen(): void {
  // Set the in-memory backstop FIRST so a failed persist below still
  // suppresses the gate for the rest of the session — the gate reaches
  // here on every slider-chart entry and callers navigate to the level
  // immediately after, so a thrown write must never re-arm the bounce.
  sliderTutorialSeenThisSession = true;
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(CIRCLES_SLIDER_TUTORIAL_KEY, '1');
  } catch (err) {
    console.error('Failed to persist circles slider tutorial flag:', err);
  }
}

export function resetTutorialForTest(mode?: GameMode): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  if (mode) {
    localStorage.removeItem(TUTORIAL_KEYS[mode]);
    return;
  }
  for (const k of Object.values(TUTORIAL_KEYS)) {
    localStorage.removeItem(k);
  }
  sliderTutorialSeenThisSession = false;
  localStorage.removeItem(CIRCLES_SLIDER_TUTORIAL_KEY);
  localStorage.removeItem(LEGACY_TUTORIAL_KEY);
  localStorage.removeItem(LEGACY_JUMP_TUTORIAL_KEY);
}
